import React from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import SourcesFileImportDialog from './SourcesFileImportDialog';
import SourcesUrlImportDialog from './SourcesUrlImportDialog';
import SourcesPasteImportDialog from './SourcesPasteImportDialog';
import SourcesApiInfoDialog from './SourcesApiInfoDialog';
import DataItemShareDialog from './DataItemShareDialog';
import {getSourcesApiBase} from './sourcesApi';
import {SOURCES_DIALOG_STACK_PROPS} from './sourcesDialogStackProps';

const SourcesSectionDialogs = ({
    folderDialogs,
    fileDeleteDialog,
    moveDialog,
    newFolderDialog,
    deleteSelectionDialog,
    addSourceDialog,
    shareDialog,
}) => {
    const showDeletionChoice = Boolean(fileDeleteDialog.deletionConflict);

    return (
        <React.Fragment>
            {folderDialogs}

            <Dialog
                open={Boolean(fileDeleteDialog.filePendingDelete)}
                onClose={fileDeleteDialog.onClose}
                fullWidth
                maxWidth="xs"
                {...SOURCES_DIALOG_STACK_PROPS}
            >
                <DialogTitle>{showDeletionChoice ? 'Choose delete scope' : 'Remove file'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        {showDeletionChoice ? (
                            <React.Fragment>
                                <DialogContentText>
                                    "{fileDeleteDialog.filePendingDelete?.name || 'Untitled file'}" exists in{' '}
                                    {fileDeleteDialog.deletionConflict?.totalReferencesToCanonical || 'multiple'}{' '}
                                    location(s). Choose what to delete.
                                </DialogContentText>
                                {fileDeleteDialog.deletionConflict?.purgeReferencedCanonicalFalseDeletesThisReferenceOnly && (
                                    <DialogContentText variant="body2" color="text.secondary">
                                        {fileDeleteDialog.deletionConflict.purgeReferencedCanonicalFalseDeletesThisReferenceOnly}
                                    </DialogContentText>
                                )}
                                {fileDeleteDialog.deletionConflict?.purgeReferencedCanonicalTrueDeletesCanonicalAndAllReferences && (
                                    <DialogContentText variant="body2" color="text.secondary">
                                        {fileDeleteDialog.deletionConflict.purgeReferencedCanonicalTrueDeletesCanonicalAndAllReferences}
                                    </DialogContentText>
                                )}
                            </React.Fragment>
                        ) : (
                            <DialogContentText>
                                Remove file "{fileDeleteDialog.filePendingDelete?.name || 'Untitled file'}"?
                            </DialogContentText>
                        )}
                        {fileDeleteDialog.error && <Alert severity="error">{fileDeleteDialog.error}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={fileDeleteDialog.onClose} disabled={fileDeleteDialog.isSubmitting}>Cancel</Button>
                    {showDeletionChoice ? (
                        <React.Fragment>
                            <Button
                                onClick={fileDeleteDialog.onConfirmPlacementOnly}
                                disabled={fileDeleteDialog.isSubmitting}
                            >
                                {fileDeleteDialog.isSubmitting ? 'Removing...' : 'Remove here only'}
                            </Button>
                            <Button
                                onClick={fileDeleteDialog.onConfirmDeleteEverywhere}
                                color="error"
                                variant="contained"
                                disabled={fileDeleteDialog.isSubmitting}
                            >
                                {fileDeleteDialog.isSubmitting ? 'Deleting...' : 'Delete everywhere'}
                            </Button>
                        </React.Fragment>
                    ) : (
                        <Button
                            onClick={fileDeleteDialog.onConfirm}
                            color="error"
                            variant="contained"
                            disabled={fileDeleteDialog.isSubmitting || !fileDeleteDialog.filePendingDelete}
                        >
                            {fileDeleteDialog.isSubmitting ? 'Removing...' : 'Remove'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={moveDialog.isOpen} onClose={moveDialog.onClose} fullWidth maxWidth="xs" {...SOURCES_DIALOG_STACK_PROPS}>
                <DialogTitle>{moveDialog.singleItemName ? 'Move File' : 'Move Selected Items'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <DialogContentText>
                            {moveDialog.singleItemName
                                ? <>Move <strong>{moveDialog.singleItemName}</strong> to:</>
                                : <>Move {moveDialog.selectedCount} selected item{moveDialog.selectedCount === 1 ? '' : 's'} to:</>
                            }
                        </DialogContentText>
                        <Select
                            value={moveDialog.targetFolderId}
                            onChange={moveDialog.onTargetChange}
                            size="small"
                            fullWidth
                            MenuProps={{sx: {zIndex: 1500}}}
                        >
                            {moveDialog.targets.map((target) => (
                                <MenuItem key={target.id} value={target.id}>
                                    {target.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {moveDialog.error && <Alert severity="error">{moveDialog.error}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={moveDialog.onClose} disabled={moveDialog.isSubmitting}>Cancel</Button>
                    <Button onClick={moveDialog.onConfirm} variant="contained" disabled={moveDialog.isConfirmDisabled}>
                        {moveDialog.isSubmitting ? 'Moving...' : 'Move'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={newFolderDialog.isOpen} onClose={newFolderDialog.onClose} fullWidth maxWidth="xs" {...SOURCES_DIALOG_STACK_PROPS}>
                <DialogTitle>New Folder With Selected</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <DialogContentText>
                            Create a folder in the current location and move {newFolderDialog.selectedCount} selected
                            item{newFolderDialog.selectedCount === 1 ? '' : 's'} into it.
                        </DialogContentText>
                        <TextField
                            label="Folder name"
                            size="small"
                            value={newFolderDialog.name}
                            onChange={newFolderDialog.onNameChange}
                            fullWidth
                        />
                        {newFolderDialog.error && <Alert severity="error">{newFolderDialog.error}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={newFolderDialog.onClose} disabled={newFolderDialog.isSubmitting}>Cancel</Button>
                    <Button onClick={newFolderDialog.onConfirm} variant="contained" disabled={newFolderDialog.isConfirmDisabled}>
                        {newFolderDialog.isSubmitting ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteSelectionDialog.isOpen} onClose={deleteSelectionDialog.onClose} fullWidth maxWidth="xs" {...SOURCES_DIALOG_STACK_PROPS}>
                <DialogTitle>Delete Selected Items</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <DialogContentText>
                            Delete {deleteSelectionDialog.selectedCount} selected
                            item{deleteSelectionDialog.selectedCount === 1 ? '' : 's'}?
                        </DialogContentText>
                        {deleteSelectionDialog.error && <Alert severity="error">{deleteSelectionDialog.error}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={deleteSelectionDialog.onClose} disabled={deleteSelectionDialog.isSubmitting}>Cancel</Button>
                    <Button
                        onClick={deleteSelectionDialog.onConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleteSelectionDialog.isConfirmDisabled}
                    >
                        {deleteSelectionDialog.isSubmitting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {addSourceDialog && (
                <React.Fragment>
                    <SourcesFileImportDialog
                        open={addSourceDialog.openKind === 'file'}
                        onClose={addSourceDialog.onClose}
                        onSubmitFiles={addSourceDialog.onSubmitFiles}
                        initialFiles={addSourceDialog.initialFiles}
                    />
                    {addSourceDialog.urlImportEnabled && (
                        <SourcesUrlImportDialog
                            open={addSourceDialog.openKind === 'url'}
                            onClose={addSourceDialog.onClose}
                            onSubmitWebImports={addSourceDialog.onSubmitWebImports}
                        />
                    )}
                    <SourcesPasteImportDialog
                        open={addSourceDialog.openKind === 'paste'}
                        onClose={addSourceDialog.onClose}
                        onSubmitWebImports={addSourceDialog.onSubmitWebImports}
                    />
                    <SourcesApiInfoDialog
                        open={addSourceDialog.openKind === 'api'}
                        onClose={addSourceDialog.onClose}
                        apiBaseUrl={getSourcesApiBase()}
                    />
                </React.Fragment>
            )}

            {shareDialog && (
                <DataItemShareDialog
                    open={shareDialog.open}
                    dataItem={shareDialog.dataItem}
                    onClose={shareDialog.onClose}
                />
            )}
        </React.Fragment>
    );
};

export default React.memo(SourcesSectionDialogs);
